import { BOOLEAN_CHOICES } from 'constants/booleanChoices';
import { NUMBER_BOOLEAN_CHOICES } from 'constants/numberBooleanChoices';

import { STATUS_CHOICES } from 'pages/Finance/RefundChargebackReport/constants/statusChoices';
import { required } from 'react-admin';
import { Controller, useFormContext } from 'react-hook-form';
import { DateTimeRangeTimezonePicker } from 'shared/mui/DatePicker/DateTimeRangeTimezonePicker/DateTimeRangeTimezonePicker';
import { FilterFieldConfig } from 'shared/mui/Filter/config';
import { useFilterContext } from 'shared/mui/Filter/context/filterContext';
import { useFilterTimezone } from 'shared/mui/Filter/hooks/useFilterTimezone';
import {
  AutocompleteInput,
  AutocompleteMultiselectInput,
  SelectInput,
} from 'shared/react-admin/Inputs';
import { ReferenceArrayInput, ReferenceInput } from 'shared/react-admin/ReferenceInputs';
import { Currency } from 'types/embedded/currency';

export const useRefundChargebackReportFilterFields = (): FilterFieldConfig[] => {
  const { control, resetField, setValue, watch } = useFormContext();
  const { timezone, onChangeTimezone } = useFilterTimezone();
  const { setAppliedFilter, appliedFilters, onReset } = useFilterContext();
  const [company, merchant, provider, gateway, appliedDate, updatedDate, summary] = watch([
    'company',
    'merchant',
    'provider',
    'gateway',
    'appliedDate',
    'updatedDate',
    'summary',
  ]);
  const isAppliedDateDisabled = !!updatedDate[0] || !!updatedDate[1];
  const isUpdatedDateDisabled = !!appliedDate[0] || !!appliedDate[1];

  return [
    {
      name: 'appliedDate',
      label: 'Время создания',
      chipType: 'dateTimeRangeTimezone',
      chipProps: {
        onDelete: onReset,
      },
      input: (
        <Controller
          control={control}
          name="appliedDate"
          render={({ field: { onChange, onBlur, value }, formState: { errors }, fieldState }) => (
            <DateTimeRangeTimezonePicker
              disabled={isAppliedDateDisabled}
              error={!!errors.appliedDate && !isAppliedDateDisabled}
              helperText={!isAppliedDateDisabled ? fieldState?.error?.message : false}
              label="Время создания / период"
              maxDate={new Date()}
              onBlur={onBlur}
              onChange={onChange}
              onChangeTimezone={onChangeTimezone}
              required
              sx={{ marginBottom: 0 }}
              timezone={timezone}
              value={value}
            />
          )}
          rules={{
            validate: {
              required: (value) => {
                if (isAppliedDateDisabled) {
                  return true;
                }
                return (!!value[0] && !!value[1]) || 'Обязательно для заполнения';
              },
            },
          }}
        />
      ),
    },
    {
      name: 'updatedDate',
      label: 'Помечен возвратом',
      chipType: 'dateTimeRangeTimezone',
      chipProps: {
        onDelete: onReset,
      },
      input: (
        <Controller
          control={control}
          name="updatedDate"
          render={({ field: { onChange, onBlur, value }, formState: { errors }, fieldState }) => (
            <DateTimeRangeTimezonePicker
              disabled={isUpdatedDateDisabled}
              error={!!errors.updatedDate && !isUpdatedDateDisabled}
              helperText={!isUpdatedDateDisabled ? fieldState?.error?.message : false}
              label="Помечен возвратом / период"
              maxDate={new Date()}
              onBlur={onBlur}
              onChange={onChange}
              onChangeTimezone={onChangeTimezone}
              required
              sx={{ margin: 0 }}
              timezone={timezone}
              value={value}
            />
          )}
          rules={{
            validate: {
              required: (value) => {
                if (isUpdatedDateDisabled) {
                  return true;
                }
                return (!!value[0] && !!value[1]) || 'Обязательно для заполнения';
              },
            },
          }}
        />
      ),
    },
    {
      name: 'company',
      label: 'Компания',
      chipType: 'autocomplete',
      chipProps: {
        resource: 'companies',
        onApply: () => {
          resetField('merchant', { keepDirty: true });
          resetField('provider', { keepDirty: true });
          resetField('gateway', { keepDirty: true });
          resetField('merchantAccount', { keepDirty: true });
          resetField('method', { keepDirty: true });
          setAppliedFilter('merchant', null);
          setAppliedFilter('provider', null);
          setAppliedFilter('gateway', []);
          setAppliedFilter('merchantAccount', []);
          setAppliedFilter('method', []);
        },
        onDelete: () => {
          resetField('merchant', { keepDirty: true });
          resetField('provider', { keepDirty: true });
          resetField('gateway', { keepDirty: true });
          resetField('merchantAccount', { keepDirty: true });
          resetField('method', { keepDirty: true });
          setAppliedFilter('merchant', null);
          setAppliedFilter('provider', null);
          setAppliedFilter('gateway', []);
          setAppliedFilter('merchantAccount', []);
          setAppliedFilter('method', []);
        },
        onReset: () => {
          setValue('merchant', appliedFilters?.['merchant']);
          setValue('provider', appliedFilters?.['provider']);
          setValue('gateway', appliedFilters?.['gateway']);
          setValue('merchantAccount', appliedFilters?.['merchantAccount']);
          setValue('method', appliedFilters?.['method']);
          setAppliedFilter('merchant', appliedFilters?.['merchant']);
          setAppliedFilter('provider', appliedFilters?.['provider']);
          setAppliedFilter('gateway', appliedFilters?.['gateway']);
          setAppliedFilter('merchantAccount', appliedFilters?.['merchantAccount']);
          setAppliedFilter('method', appliedFilters?.['method']);
        },
      },
      input: (variant) => (
        <ReferenceInput
          filter={{ exists: { merchants: true }, archive: false }}
          perPage={100}
          reference="companies"
          source="company"
        >
          <AutocompleteInput
            filterToQuery={(searchText: string) => ({ name: searchText })}
            fullWidth
            helperText={false}
            label="Компания"
            {...(variant === 'field' && {
              onChange: () => {
                setValue('merchant', null);
                setValue('provider', null);
                setValue('gateway', []);
                setValue('merchantAccount', []);
                setValue('method', []);
              },
            })}
            optionText="name"
            variant="outlined"
          />
        </ReferenceInput>
      ),
    },
    {
      name: 'merchant',
      label: 'Мерчант',
      chipType: 'autocomplete',
      chipProps: {
        resource: 'merchants',
        onApply: () => {
          resetField('provider', { keepDirty: true });
          resetField('gateway', { keepDirty: true });
          resetField('merchantAccount', { keepDirty: true });
          resetField('method', { keepDirty: true });
          setAppliedFilter('provider', null);
          setAppliedFilter('gateway', []);
          setAppliedFilter('merchantAccount', []);
          setAppliedFilter('method', []);
        },
        onDelete: () => {
          resetField('provider', { keepDirty: true });
          resetField('gateway', { keepDirty: true });
          resetField('merchantAccount', { keepDirty: true });
          resetField('method', { keepDirty: true });
          setAppliedFilter('provider', null);
          setAppliedFilter('gateway', []);
          setAppliedFilter('merchantAccount', []);
          setAppliedFilter('method', []);
        },
        onReset: () => {
          setValue('provider', appliedFilters?.['provider']);
          setValue('gateway', appliedFilters?.['gateway']);
          setValue('merchantAccount', appliedFilters?.['merchantAccount']);
          setValue('method', appliedFilters?.['method']);
          setAppliedFilter('provider', appliedFilters?.['provider']);
          setAppliedFilter('gateway', appliedFilters?.['gateway']);
          setAppliedFilter('merchantAccount', appliedFilters?.['merchantAccount']);
          setAppliedFilter('method', appliedFilters?.['method']);
        },
      },
      input: (variant) => (
        <ReferenceInput
          enableGetChoices={() => !!company}
          filter={{
            companies: company,
            exists: { merchantAccounts: true },
            archive: false,
          }}
          perPage={100}
          reference="merchants"
          source="merchant"
        >
          <AutocompleteInput
            {...(variant === 'field' && {
              disabled: !company,
              disabledHint: 'Сначала выберите компанию',
              onChange: () => {
                setValue('provider', null);
                setValue('gateway', []);
                setValue('merchantAccount', []);
                setValue('method', []);
              },
            })}
            filterToQuery={(searchText: string) => ({ name: searchText })}
            fullWidth
            helperText={false}
            label="Мерчант"
            optionText="name"
            variant="outlined"
          />
        </ReferenceInput>
      ),
    },
    {
      name: 'provider',
      label: 'Провайдер',
      chipType: 'autocomplete',
      chipProps: {
        resource: 'providers',
        onApply: () => {
          resetField('gateway', { keepDirty: true });
          resetField('merchantAccount', { keepDirty: true });
          resetField('method', { keepDirty: true });
          setAppliedFilter('gateway', []);
          setAppliedFilter('merchantAccount', []);
          setAppliedFilter('method', []);
        },
        onDelete: () => {
          resetField('gateway', { keepDirty: true });
          resetField('merchantAccount', { keepDirty: true });
          resetField('method', { keepDirty: true });
          setAppliedFilter('gateway', []);
          setAppliedFilter('merchantAccount', []);
          setAppliedFilter('method', []);
        },
        onReset: () => {
          setValue('gateway', appliedFilters?.['gateway']);
          setValue('merchantAccount', appliedFilters?.['merchantAccount']);
          setValue('method', appliedFilters?.['method']);
          setAppliedFilter('gateway', appliedFilters?.['gateway']);
          setAppliedFilter('merchantAccount', appliedFilters?.['merchantAccount']);
          setAppliedFilter('method', appliedFilters?.['method']);
        },
      },
      input: (variant) => (
        <ReferenceInput
          enableGetChoices={() => !!merchant}
          filter={{ merchantAccounts: { merchants: merchant } }}
          perPage={100}
          reference="providers"
          sort={{ field: 'name', order: 'ASC' }}
          source="provider"
        >
          <AutocompleteInput
            clearOnBlur={false}
            {...(variant === 'field' && {
              disabled: !merchant,
              disabledHint: 'Сначала выберите мерчанта',
              onChange: () => {
                setValue('merchantAccount', []);
                setValue('gateway', []);
                setValue('method', []);
              },
            })}
            filterToQuery={(searchText: string) => ({ name: searchText })}
            fullWidth
            helperText={false}
            label="Провайдер"
            optionText="name"
            variant="outlined"
          />
        </ReferenceInput>
      ),
    },
    {
      name: 'merchantAccount',
      label: 'Счета мерчантов',
      chipType: 'multiselect',
      chipProps: {
        resource: 'merchant_accounts',
        limitTags: 1,
      },
      input: (variant) => (
        <ReferenceArrayInput
          enableGetChoices={() => !!merchant && !!provider}
          filter={{
            merchants: merchant,
            provider,
          }}
          perPage={100}
          reference="merchant_accounts"
          source="merchantAccount"
        >
          <AutocompleteMultiselectInput
            {...(variant === 'field' && {
              disabled: !provider,
              disabledHint: 'Сначала выберите провайдера',
            })}
            filterToQuery={(searchText: string) => ({ name: searchText })}
            helperText={false}
            label="Счета мерчантов"
            name="merchantAccount"
            optionText="name"
          />
        </ReferenceArrayInput>
      ),
    },
    {
      name: 'gateway',
      label: 'Шлюзы',
      chipType: 'multiselect',
      chipProps: {
        resource: 'gateways',
        limitTags: 1,
        onApply: () => {
          resetField('method', { keepDirty: true });
          setAppliedFilter('method', []);
        },
        onDelete: () => {
          resetField('method', { keepDirty: true });
          setAppliedFilter('method', []);
        },
        onReset: () => {
          setValue('method', appliedFilters?.['method']);
          setAppliedFilter('method', appliedFilters?.['method']);
        },
      },
      input: (variant) => (
        <ReferenceArrayInput
          enableGetChoices={() => !!merchant && !!provider}
          filter={{
            merchantAccounts: { merchants: merchant },
            provider,
          }}
          perPage={100}
          reference="gateways"
          source="gateway"
        >
          <AutocompleteMultiselectInput
            {...(variant === 'field' && {
              disabled: !provider,
              disabledHint: 'Сначала выберите провайдера',
              onChange: () => {
                setValue('method', []);
              },
            })}
            filterToQuery={(searchText: string) => ({ name: searchText })}
            helperText={false}
            label="Шлюзы"
            name="gateway"
            optionText="name"
          />
        </ReferenceArrayInput>
      ),
    },
    {
      name: 'method',
      label: 'Методы',
      chipType: 'multiselect',
      chipProps: {
        resource: 'methods',
        limitTags: 1,
      },
      input: (variant) => (
        <ReferenceArrayInput
          filter={{
            gateway,
          }}
          perPage={100}
          reference="methods"
          source="method"
        >
          <AutocompleteMultiselectInput
            {...(variant === 'field' && {
              disabled: !gateway?.length,
              disabledHint: 'Сначала выберите шлюз',
            })}
            helperText={false}
            label="Методы"
            name="method"
            optionText="name"
          />
        </ReferenceArrayInput>
      ),
    },
    {
      name: 'status',
      label: 'Рефанды / Чарджбеки',
      chipType: 'multiselect',
      chipProps: {
        resource: 'new/order_statuses',
        options: STATUS_CHOICES,
      },
      input: (
        <AutocompleteMultiselectInput
          helperText={false}
          label="Рефанды / Чарджбеки"
          name="status"
          options={STATUS_CHOICES}
        />
      ),
    },
    {
      name: 'impactToBalance',
      label: 'Движение по балансу',
      chipType: 'text',
      chipProps: {
        getName: (filterValue: string) =>
          NUMBER_BOOLEAN_CHOICES.find((value) => value.id === filterValue)?.label || '',
      },
      input: (
        <SelectInput
          choices={NUMBER_BOOLEAN_CHOICES}
          defaultValue={null}
          fullWidth
          helperText={false}
          label="Движение по балансу"
          name="impactToBalance"
          optionText="label"
          source="impactToBalance"
          variant="outlined"
        />
      ),
    },
    {
      name: 'currency',
      label: 'Валюты',
      chipType: 'multiselect',
      chipProps: {
        resource: 'currencies',
        limitTags: 1,
      },
      input: (
        <ReferenceArrayInput
          filter={{ active: true }}
          perPage={50}
          reference="currencies"
          sort={{ field: 'id', order: 'ASC' }}
          source="currency"
        >
          <AutocompleteMultiselectInput
            filterToQuery={(searchText: string) => ({ currency: searchText })}
            fullWidth
            getOptionLabel={(record: Currency) => `${record?.alphaCode}, ${record?.name}`}
            helperText={false}
            label="Валюты"
            name="currency"
            sx={{
              '.MuiTextField-root': {
                marginTop: 0,
              },
            }}
          />
        </ReferenceArrayInput>
      ),
    },
    {
      name: 'summary',
      label: 'Запросить сводку',
      chipType: 'text',
      chipProps: {
        getName: (filterValue: boolean) =>
          BOOLEAN_CHOICES.find((value) => value.id === filterValue)?.name || '',
        onApply: () => {
          resetField('conversionCurrency', { keepDirty: true });
          setAppliedFilter('conversionCurrency', null);
        },
        onDelete: () => {
          resetField('conversionCurrency', { keepDirty: true });
          setAppliedFilter('conversionCurrency', null);
        },
      },
      input: (variant) => (
        <SelectInput
          choices={BOOLEAN_CHOICES}
          defaultValue={false}
          fullWidth
          helperText={false}
          id="summary"
          label="Запросить сводку"
          name="summary"
          {...(variant === 'field' && {
            onChange: () => {
              setValue('conversionCurrency', null);
            },
          })}
          source="summary"
          validate={required()}
          variant="outlined"
        />
      ),
    },
    {
      name: 'conversionCurrency',
      label: 'Валюта конвертации',
      chipType: 'autocomplete',
      chipProps: {
        resource: 'currencies',
        onDelete: () => {
          resetField('summary', { keepDirty: true });
          setAppliedFilter('summary', false);
        },
      },
      input: (
        <ReferenceInput
          filter={{ active: true }}
          name="conversionCurrency"
          perPage={50}
          reference="currencies"
          sort={{ field: 'id', order: 'ASC' }}
          source="conversionCurrency"
        >
          <AutocompleteInput
            filterToQuery={(searchText: string) => ({ currency: searchText })}
            fullWidth
            helperText={false}
            label="Валюта конвертации"
            name="conversionCurrency"
            optionText={(record: Currency) => `${record?.alphaCode}, ${record?.name}`}
            source="conversionCurrency"
            validate={summary ? required() : undefined}
            variant="outlined"
          />
        </ReferenceInput>
      ),
    },
  ];
};
